import { useEffect, useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import api from "../lib/api";
import { deSeenStamp } from "../lib/dateFormat";

/**
 * 24 Jun 2026 · Goldener „Gelesen"-Hinweis (Read-Receipt für Zuweisungen).
 * „Gold" ist NUR ein Indikator — keine der vier System-Statusfarben.
 *
 *   markSeen(entityType, id)        — POST /seen/{type}/{id} (Backend no-op,
 *                                     wenn der Aufrufer nicht der Verantwortliche ist)
 *   useMarkSeen(type, id, active)   — stempelt beim Öffnen, liefert Server-State
 *   <SeenDot .../>                  — goldener Punkt in Listen (mir zugewiesen + ungeöffnet)
 *   <SeenStatusLine .../>           — Admin-Detailzeile mit 3 Zuständen
 */
const GOLD = "#C9A961";

export async function markSeen(entityType, id) {
  try {
    const { data } = await api.post(`/seen/${entityType}/${id}`);
    return data;
  } catch {
    return null;
  }
}

export function useMarkSeen(entityType, id, active = true) {
  const [seen, setSeen] = useState(null);
  useEffect(() => {
    if (!active || !id) return undefined;
    let cancelled = false;
    (async () => {
      const data = await markSeen(entityType, id);
      if (!cancelled && data) setSeen(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [entityType, id, active]);
  return seen;
}

export function SeenDot({ entity, viewerId, assigneeField = "assigned_to", testid }) {
  if (!entity || !viewerId) return null;
  const assignee = entity[assigneeField];
  const unread = assignee && assignee === viewerId && !entity.seen_by_assignee_at;
  if (!unread) return null;
  return (
    <span
      data-testid={testid || "seen-unread-dot"}
      title="Neu zugewiesen — noch nicht geöffnet"
      className="inline-block w-2.5 h-2.5 rounded-full shrink-0 animate-pulse"
      style={{ background: GOLD, boxShadow: "0 0 0 2px rgba(201,169,97,0.25)" }}
    />
  );
}

// 24 Jun 2026 · „Ungeöffnete Zuweisung" — dieselbe Definition wie der goldene
// Sidebar-Zähler: die Entität ist zugewiesen UND von der zugewiesenen Person
// noch nicht geöffnet (seen_by_assignee_at leer). `viewerId` gesetzt → nur
// eigene Zuweisungen (Concierge/Impersonation); null → beliebiger Assignee
// (Admin-Gesamtsicht).
export function isUnseenAssignment(row, viewerId, assigneeField = "assigned_to") {
  const assignee = row?.[assigneeField];
  if (!assignee || row?.seen_by_assignee_at) return false;
  return viewerId ? assignee === viewerId : true;
}

// Goldene Schnellfilter-Pille „Nur ungeöffnet" — passend zum goldenen Zähler.
export function SeenFilterToggle({ active, count, onToggle, testidBase = "seen-filter" }) {
  if (!active && !count) return null;
  const GOLD = "#C9A961";
  if (active) {
    return (
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${testidBase}-active`}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full hover:opacity-90 transition-opacity"
        style={{ background: GOLD, color: "#3A2E12" }}
        aria-label="Filter zurücksetzen — alle anzeigen"
      >
        <Eye className="w-3.5 h-3.5" strokeWidth={2} />
        Nur ungeöffnet · {count}
        <X className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={`${testidBase}-inactive`}
      className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase rounded-full border transition-colors text-navy hover:opacity-90"
      style={{ borderColor: "rgba(201,169,97,0.65)" }}
      aria-label={`Nur ungeöffnete Zuweisungen anzeigen (${count})`}
    >
      <span className="w-2 h-2 rounded-full inline-block" style={{ background: GOLD }} />
      Nur ungeöffnet ({count})
    </button>
  );
}

export function SeenStatusLine({ entity, className = "", testid = "seen-status-line" }) {
  if (!entity) return null;
  const seenAt = entity.seen_by_assignee_at;
  const name = entity.seen_by_assignee_name || "—";
  const activity = entity.last_activity_at;

  let state;
  let text;
  let highlight = false;
  if (!seenAt) {
    state = "unopened";
    text = "Noch nicht geöffnet";
  } else if (!activity || activity <= seenAt) {
    state = "no-activity";
    text = `Gesehen von ${name} am ${deSeenStamp(seenAt)} — seitdem keine Bearbeitung`;
    highlight = true;
  } else {
    state = "active";
    text = `Gesehen von ${name} am ${deSeenStamp(seenAt)} — zuletzt bearbeitet am ${deSeenStamp(activity)}`;
  }

  const style =
    state === "unopened"
      ? { color: "#6B7280", background: "rgba(107,114,128,0.10)" }
      : highlight
        ? { color: "#8A7635", background: "rgba(201,169,97,0.18)", border: `1px solid ${GOLD}` }
        : { color: "#4B7A55", background: "rgba(75,122,85,0.10)" };
  const Icon = state === "unopened" ? EyeOff : Eye;

  return (
    <span
      data-testid={testid}
      data-seen-state={state}
      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md leading-snug ${className}`}
      style={style}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
      {text}
    </span>
  );
}
